/* ============================================================
   Wiredan Vault Protocol - Satellite Context (V13.2.0)
   Update: 24/7 TELEMETRY & GPS PRECISION SYNC
   Status: 100% OPERATIONAL | D1-CLUSTER-READY | GLOBAL SCALE
   ============================================================ */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const SatelliteContext = createContext();

export const SatelliteProvider = ({ children }) => {
  const { user } = useAuth();
  const [isNodeActive, setIsNodeActive] = useState(false); 
  const [isSyncing, setIsSyncing] = useState(false);
  const [currentAsset, setCurrentAsset] = useState("Maize");
  const [satelliteData, setSatelliteData] = useState(null);
  const [coords, setCoords] = useState({ lat: null, lng: null });

  // --- UUID-STRICT IDENTITY LOCK ---
  const ownerId = user?.uuid || localStorage.getItem("agritrade_uuid");

  // --- 1. CLUSTER-AWARE PERSISTENCE ENGINE ---
  const syncInitialState = useCallback(async () => {
    if (!ownerId || ownerId === "undefined" || ownerId === "null") return;
    
    try {
      const res = await fetch(`/api/user/farms`, {
        method: 'GET',
        headers: {
          'X-UUID': ownerId,
          'X-User-Id': ownerId,
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          // Priority: Find existing data for current asset, fallback to first entry
          const activeAssetRow = data.find(f => f.crop_type?.toLowerCase() === currentAsset.toLowerCase()) || data[0];
          
          const isActive = activeAssetRow.is_node_active === 1 || activeAssetRow.status === 'active';
          
          setIsNodeActive(isActive);
          if (!currentAsset) setCurrentAsset(activeAssetRow.crop_type || "Maize");
          
          setSatelliteData(activeAssetRow);
          
          if (activeAssetRow.latitude && activeAssetRow.longitude) {
            setCoords({ 
              lat: Number(activeAssetRow.latitude), 
              lng: Number(activeAssetRow.longitude) 
            });
          }
        }
      }
    } catch (e) {
      console.error("Orbital Sync Persistence Failed", e);
    }
  }, [ownerId, currentAsset]);

  useEffect(() => {
    syncInitialState();
  }, [syncInitialState]);

  // --- 2. LOCAL CACHE & STORAGE SYNC ---
  useEffect(() => {
    if (user?.latitude && user?.longitude) {
      setCoords({ lat: Number(user.latitude), lng: Number(user.longitude) });
    } else {
      const savedUser = JSON.parse(localStorage.getItem("agritrade_user") || "{}");
      if (savedUser.latitude && savedUser.longitude) {
        setCoords({ lat: Number(savedUser.latitude), lng: Number(savedUser.longitude) });
      }
    }
    
    if (user?.uuid) {
      localStorage.setItem("agritrade_uuid", user.uuid);
    }
  }, [user]);

  const togglePower = async () => {
    if (!ownerId) {
      toast.error("Identity Lock Required. Please Login.");
      return;
    }

    // --- 3. TERMINATION PROTOCOL (Surgical Deactivation) ---
    if (isNodeActive) {
      setIsSyncing(true);
      try {
        const res = await fetch('/api/user/farms', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-UUID': ownerId,
            'X-User-Id': ownerId
          },
          body: JSON.stringify({ 
            uuid: ownerId, 
            crop_type: currentAsset, 
            is_node_active: 0,
            status: 'inactive'
          })
        });

        if (res.ok) {
          setIsNodeActive(false);
          setSatelliteData(null);
          toast("Satellite Link Terminated", { icon: '📡' });
        } else {
          throw new Error("Handshake Refused");
        }
      } catch (e) { 
        setIsNodeActive(false);
        toast.error("Emergency Local Deactivation"); 
      } finally {
        setIsSyncing(false);
      }
      return;
    }

    // --- 4. ACTIVATION PROTOCOL (Precision Location Handshake) ---
    setIsSyncing(true);
    const toastId = toast.loading("Establishing Global Node Lock...");

    if (!navigator.geolocation) {
      toast.error("GPS Hardware Not Found", { id: toastId });
      setIsSyncing(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        // Using REAL/DECIMAL precision for 24/7 Weather API accuracy
        const nLat = Number(latitude.toFixed(8)); 
        const nLng = Number(longitude.toFixed(8));
        setCoords({ lat: nLat, lng: nLng });

        try {
          const res = await fetch('/api/user/farms', {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json', 
              'X-UUID': ownerId,
              'X-User-Id': ownerId 
            },
            body: JSON.stringify({ 
              uuid: ownerId, 
              crop_type: currentAsset,
              latitude: nLat, 
              longitude: nLng,
              is_node_active: 1,
              status: 'active'
            })
          });

          if (res.ok) {
            const savedUser = JSON.parse(localStorage.getItem("agritrade_user") || "{}");
            localStorage.setItem("agritrade_user", JSON.stringify({
              ...savedUser,
              latitude: nLat,
              longitude: nLng
            }));

            setIsNodeActive(true);
            syncInitialState();
            toast.success("ORBITAL LINK ONLINE", { id: toastId });
          } else { 
            throw new Error("Registry Error"); 
          }
        } catch (err) {
          toast.error("Link Handshake Failed", { id: toastId });
        } finally { 
          setIsSyncing(false); 
        }
      },
      (error) => {
        const errorMsg = error.code === 1 ? "GPS Permission Denied" : "GPS Signal Timeout";
        toast.error(errorMsg, { id: toastId });
        setIsSyncing(false);
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  return (
    <SatelliteContext.Provider value={{ 
      isNodeActive, 
      setIsNodeActive, 
      isSyncing, 
      currentAsset, 
      setCurrentAsset, 
      togglePower, 
      satelliteData,
      setSatelliteData,
      coords 
    }}>
      {children}
    </SatelliteContext.Provider>
  );
};

export const useSatellite = () => useContext(SatelliteContext);